const SUPPORT_AI_CUSTOMER_DOCS_ORIGIN = 'https://docs.avalai.ir';
const SUPPORT_AI_CUSTOMER_DOCS_AUTHORITY = 'docs.avalai.ir';
const SUPPORT_AI_CUSTOMER_KNOWLEDGE_BASE_PATH = '/platform/support/knowledge-base';
const SUPPORT_AI_CUSTOMER_SOURCE_HREF_MAX_LENGTH = 2048;
const MAX_URL_DECODE_PASSES = 4;
const UNSAFE_URL_TEXT_RE = /[\u0000-\u001F\u007F-\u009F\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069<>"'`]/u;
const URL_WHITESPACE_RE = /\s/u;

export const SUPPORT_AI_CUSTOMER_MAX_POLL_DURATION_MS = 5 * 60 * 1000;

export type SupportProfileCommitGuard = () => boolean;

export function runGuardedSupportProfileCommit(
  commitGuard: SupportProfileCommitGuard,
  commit: () => void
): boolean {
  try {
    if (!commitGuard()) return false;
  } catch {
    return false;
  }

  commit();
  return true;
}

function decodeAndValidateUrlPart(value: string, rejectDotSegments: boolean): string | null {
  let current = value;

  for (let pass = 0; pass <= MAX_URL_DECODE_PASSES; pass += 1) {
    if (URL_WHITESPACE_RE.test(current) || UNSAFE_URL_TEXT_RE.test(current) || current.includes('\\')) {
      return null;
    }
    if (rejectDotSegments && current.split('/').some(segment => segment === '.' || segment === '..')) {
      return null;
    }

    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return null;
    }

    if (decoded === current) return current;
    if (pass === MAX_URL_DECODE_PASSES) return null;
    current = decoded;
  }

  return null;
}

function hasExactDocsAuthority(input: string): boolean {
  if (!input.startsWith('https://')) return false;
  const authorityStart = 'https://'.length;
  const suffixStart = input.slice(authorityStart).search(/[/?#]/);
  const authorityEnd = suffixStart === -1 ? input.length : authorityStart + suffixStart;
  return input.slice(authorityStart, authorityEnd) === SUPPORT_AI_CUSTOMER_DOCS_AUTHORITY;
}

function rawPathFromUrl(input: string, absolute: boolean): string {
  const queryIndex = input.indexOf('?');
  const hashIndex = input.indexOf('#');
  const suffixStart = Math.min(
    queryIndex === -1 ? input.length : queryIndex,
    hashIndex === -1 ? input.length : hashIndex
  );

  if (!absolute) return input.slice(0, suffixStart);

  const authorityStart = input.indexOf('://') + 3;
  const pathStart = input.indexOf('/', authorityStart);
  return pathStart === -1 || pathStart >= suffixStart ? '/' : input.slice(pathStart, suffixStart);
}

function hasSafeQueryAndHash(input: string): boolean {
  const suffixStart = input.search(/[?#]/);
  if (suffixStart === -1) return true;
  return decodeAndValidateUrlPart(input.slice(suffixStart), false) !== null;
}

export function getSafeSupportAICustomerSourceHref(input: unknown): string | null {
  if (
    typeof input !== 'string' ||
    !input ||
    input.length > SUPPORT_AI_CUSTOMER_SOURCE_HREF_MAX_LENGTH ||
    input !== input.trim() ||
    URL_WHITESPACE_RE.test(input)
  ) {
    return null;
  }
  if (input.includes('\\') || !hasSafeQueryAndHash(input)) return null;

  const isAbsolute = hasExactDocsAuthority(input);
  if (!isAbsolute && (!input.startsWith('/') || input.startsWith('//'))) return null;

  if (isAbsolute) {
    let parsed: URL;
    try {
      parsed = new URL(input);
    } catch {
      return null;
    }

    if (
      parsed.origin !== SUPPORT_AI_CUSTOMER_DOCS_ORIGIN ||
      parsed.protocol !== 'https:' ||
      parsed.hostname !== 'docs.avalai.ir' ||
      parsed.username ||
      parsed.password ||
      parsed.port
    ) {
      return null;
    }
  }

  const decodedPath = decodeAndValidateUrlPart(rawPathFromUrl(input, isAbsolute), true);
  if (decodedPath === null || !decodedPath.startsWith('/')) return null;

  if (
    !isAbsolute &&
    decodedPath !== SUPPORT_AI_CUSTOMER_KNOWLEDGE_BASE_PATH &&
    !decodedPath.startsWith(`${SUPPORT_AI_CUSTOMER_KNOWLEDGE_BASE_PATH}/`)
  ) {
    return null;
  }

  return input;
}

interface SupportAICustomerReplyCandidate {
  author?: { author_type?: string | null } | null;
  customer_ai?: Api.Support.SupportAICustomerReplyMeta | null;
}

export function isVerifiedSupportAICustomerReply(
  reply: SupportAICustomerReplyCandidate
): reply is SupportAICustomerReplyCandidate & {
  customer_ai: Api.Support.SupportAICustomerReplyMeta;
} {
  return reply.author?.author_type === 'bot' && Boolean(reply.customer_ai);
}

export interface SupportAICustomerBootstrapInput {
  isVerifiedOwner: boolean;
  hasVerifiedAIContext: boolean;
  status: Api.Support.SupportAICustomerStatus | null;
}

export interface SupportAICustomerBootstrapDecision {
  shouldLoadStatus: boolean;
  shouldShowStatus: boolean;
  shouldPoll: boolean;
}

export function resolveSupportAICustomerBootstrap(
  input: SupportAICustomerBootstrapInput
): SupportAICustomerBootstrapDecision {
  const shouldLoadStatus = input.isVerifiedOwner;
  const hasAuthoritativeAIContext = input.status !== null && input.status.state !== 'not_requested';
  const shouldShowStatus = shouldLoadStatus && (
    input.hasVerifiedAIContext || hasAuthoritativeAIContext
  );
  const shouldPoll = shouldLoadStatus && input.status?.pending === true && (
    input.status.state === 'queued' ||
    input.status.state === 'processing' ||
    input.status.state === 'clarification_queued'
  );

  return { shouldLoadStatus, shouldShowStatus, shouldPoll };
}

export async function bootstrapSupportAICustomerStatus(
  input: Omit<SupportAICustomerBootstrapInput, 'status'>,
  loadAndSchedulePendingStatus: () => Promise<Api.Support.SupportAICustomerStatus | null>
): Promise<SupportAICustomerBootstrapDecision> {
  const initialDecision = resolveSupportAICustomerBootstrap({ ...input, status: null });
  if (!initialDecision.shouldLoadStatus) return initialDecision;

  const status = await loadAndSchedulePendingStatus();
  return resolveSupportAICustomerBootstrap({ ...input, status });
}

function normalizeSupportPrincipalId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized || null;
}

export function getAuthenticatedSupportPrincipalKey(
  isAuthenticated: unknown,
  uuid: unknown
): string | null {
  const principalId = normalizeSupportPrincipalId(uuid);
  if (isAuthenticated !== true || !principalId) return null;
  return JSON.stringify(['authenticated', principalId]);
}

export function isSupportPrincipalProfileCurrent(
  principalKey: string | null,
  profileId: unknown
): boolean {
  return principalKey !== null && principalKey === getAuthenticatedSupportPrincipalKey(true, profileId);
}

interface SupportPrincipalTransitionHooks {
  clearSensitiveState(): void;
  reloadForPrincipal(principalKey: string): Promise<void> | void;
}

export async function runSupportPrincipalTransition(
  previousPrincipalKey: string | null,
  nextPrincipalKey: string | null,
  hooks: SupportPrincipalTransitionHooks
): Promise<boolean> {
  if (previousPrincipalKey === nextPrincipalKey) return false;

  hooks.clearSensitiveState();
  if (nextPrincipalKey) await hooks.reloadForPrincipal(nextPrincipalKey);
  return true;
}

export function replaceWithAuthoritativeSupportTicket<T>(
  _previousTicket: unknown,
  serverTicket: T
): T {
  return serverTicket;
}

export interface SupportAIHumanReviewAttempt {
  logicalKey: string;
  idempotencyKey: string;
}

export function resolveSupportAIHumanReviewAttempt(
  currentAttempt: SupportAIHumanReviewAttempt | null,
  logicalKey: string,
  createIdempotencyKey: () => string
): SupportAIHumanReviewAttempt {
  if (currentAttempt?.logicalKey === logicalKey) return currentAttempt;
  return { logicalKey, idempotencyKey: createIdempotencyKey() };
}

export function settleSupportAIHumanReviewAttempt<T>(
  attempt: SupportAIHumanReviewAttempt,
  result: T | null
): SupportAIHumanReviewAttempt | null {
  return result === null ? attempt : null;
}

export function isSupportAICustomerPendingState(state: Api.Support.SupportAICustomerState): boolean {
  return state === 'queued' || state === 'processing' || state === 'clarification_queued';
}

export function isSupportAICustomerTerminalState(state: Api.Support.SupportAICustomerState): boolean {
  return (
    state === 'not_requested' ||
    state === 'answered' ||
    state === 'answered_after_clarification' ||
    state === 'human_requested' ||
    state === 'human_required' ||
    state === 'stopped'
  );
}

export function getSupportAICustomerPollDelay(
  status: Api.Support.SupportAICustomerStatus,
  elapsedMs: number
): number | null {
  if (
    elapsedMs >= SUPPORT_AI_CUSTOMER_MAX_POLL_DURATION_MS ||
    status.pending === false ||
    isSupportAICustomerTerminalState(status.state) ||
    !isSupportAICustomerPendingState(status.state)
  ) {
    return null;
  }

  const seconds = Number.isFinite(status.poll_after_seconds) ? status.poll_after_seconds : 1;
  return Math.min(60, Math.max(1, seconds)) * 1000;
}

export interface SupportAICustomerVoteTransition {
  previousVote: Api.Support.SupportAICustomerVote;
  optimisticVote: Api.Support.SupportAICustomerVote;
}

export function createSupportAICustomerVoteTransition(
  previousVote: Api.Support.SupportAICustomerVote,
  optimisticVote: Api.Support.SupportAICustomerVote
): SupportAICustomerVoteTransition {
  return { previousVote, optimisticVote };
}

export function rollbackSupportAICustomerVote(
  transition: SupportAICustomerVoteTransition
): Api.Support.SupportAICustomerVote {
  return transition.previousVote;
}
